// ── Feature flags (M3.4) ──────────────────────────────────────────────────────
//
// Single, reversible source of truth for half-built / not-yet-shipped product
// surfaces that must stay hidden from real users in production. The teardown
// (docs/reports/productTeardown2026.md) flagged prod-reachable stubs; this is
// where we gate them.
//
// HOW TO RE-ENABLE A FEATURE WHEN IT SHIPS:
//   Flip its flag to `true` below (or wire it to a VITE_* env var). That's it —
//   the route, nav entry and/or tab come back. Nothing here is deleted, only
//   gated, so re-enabling is a one-line change.
//
// Convention:
//   - `false`            -> hidden in every environment (prod + dev).
//   - `import.meta.env.DEV` -> visible only on a developer's local build, hidden
//                              in production. Used for surfaces we still want to
//                              poke at locally but must never ship.

export const FEATURE_FLAGS = {
  /**
   * Backlinks tab inside Content Studio. Placeholder "coming soon" surface — the
   * real feature is blocked on DataForSEO backlinks data. Hidden everywhere
   * until that ships. Set to `true` to bring the tab back.
   */
  BACKLINKS: false,

  /**
   * A/B testing page. Half-built experiment surface. Visible locally for dev
   * work, hidden in production. (Preserves the previous `import.meta.env.DEV`
   * gate, now named and centralised.)
   */
  AB_TESTING: import.meta.env.DEV,

  /**
   * Geographic / geo-grid analytics page. Same treatment as A/B testing —
   * local-only until it's production-ready.
   */
  GEOGRAPHIC: import.meta.env.DEV,

  /**
   * Google Business Profile — the dedicated /google-business page + nav item.
   * LIVE as of 2026-07-02: the DataForSEO live provider is wired + domain-verified
   * (backend `GBP_LIVE_PROVIDER=true`, prod rev 00439-kkj), so this reads real data.
   * Mock only shows when the backend is on the mock provider (dev without the flag),
   * and it carries a "preview data" pill either way — never presenting a fixture as
   * a measurement.
   */
  GBP: true,

  /**
   * "Analytics" tab on the per-project dashboard (ProjectDashboardPage). Renders
   * only an "Analytics Coming Soon" placeholder today — a dead-end clickable tab.
   * Hidden everywhere until the real analytics surface ships. Flip to `true`
   * (or `import.meta.env.DEV`) to bring the tab back.
   */
  ANALYTICS_TAB: false,

  /**
   * "Mission Control" — the flagship "just tell me what to do" project landing
   * view (docs/plans/plan-mission-control.md, Phase A). When ON it becomes the
   * FIRST item in the sidebar's Overview section (above "Dashboard") on its own
   * `/mission-control` route, AND the default post-login landing (Sam, A5
   * 2026-07-08); the existing Dashboard/Overview is retained as item 2, unchanged.
   * When OFF the app is byte-identical to today: no nav item, the route redirects
   * to `/`, and login lands on `/` as before. Phase A recomposes existing
   * dashboard components behind the missions endpoint (no new backend source).
   */
  MISSION_CONTROL: true,
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/** Convenience accessor. `isFeatureEnabled('BACKLINKS')` reads better at call sites. */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  return FEATURE_FLAGS[flag] === true;
}
